<template>
  <BuildingSectionCard
    title="Location"
    :title-badge="building.location.isAddressHidden ? 'Hidden on platforms' : undefined"
    :form="LocationForm"
    has-info
  >
    <div v-if="coordinates" class="map">
      <Map :coordinates="coordinates" />
    </div>
    <!-- The lookup gave the address no coordinates; the address itself still reads and opens -->
    <Hint v-else icon="info-circle" data-testid="map-unavailable">
      Map unavailable for this address — check the street and ZIP.
    </Hint>

    <View justify="space-between" align="center" gap="md" wrap="wrap">
      <View direction="column" gap="xs" class="address">
        <Text variant="label-medium">{{ building.location.address }}</Text>
        <Text variant="body-medium" color="secondary">{{ buildingCityLine(building.location) }}</Text>
      </View>
      <Link :to="buildingMapsUrl(building.location)" target="_blank" rel="noopener" class="open-in-maps">
        <Icon name="link-external-01" size="small" />
        <span>Open in Maps</span>
      </Link>
    </View>
  </BuildingSectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Hint, Icon, Link, Text, View } from '@tallo/design-system';
import { BuildingDto, LocationForm, buildingCityLine, buildingMapsUrl } from '@tallo/investor/property';
import { Coordinates, Map } from '@tallo/property';

import BuildingSectionCard from '../../../components/building-section-card.vue';

/** Where the building is: the map, the address every unit shares, and a way to open it elsewhere */
const props = defineProps<{ building: BuildingDto }>();

const coordinates = computed<Coordinates | null>(() => {
  const { latitude, longitude } = props.building.location;

  return latitude && longitude ? { latitude, longitude } : null;
});
</script>

<style scoped lang="scss">
// Fills the card, so the map stretches to the row's height beside the building card
.map {
  flex: 1;
  min-height: 9rem;
  border-radius: var(--t-border-radius-sm);
  overflow: hidden;

  :deep(> *) {
    height: 100%;
    min-height: 9rem;
  }
}

.address {
  min-width: 0;
  overflow-wrap: anywhere;
}

.open-in-maps {
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-xs);
}
</style>
